import { useState, useEffect, Fragment } from 'react'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { includes } from 'lodash-es'
import styled from '@emotion/styled'

import { selectWebviewName } from 'store/webview/webviewSlice'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import { useAlbum, usePhotos, usePhotoClassifications } from 'utils/api/hooks'
import { setPhotoViewed } from 'utils/api'
import { useGtmAlbumPageView } from 'utils/hooks/analytics'
import { useSearchPanel } from 'components/common/PopupSearchPanel/hooks'
import ContentWrapper, {
  MaxWidthWrapper,
} from 'components/common/ContentWrapper'
import NowrapDiv from 'components/common/NowrapDiv'
import { EyeSVG, CloseSVG } from 'components/common/SVGElements'
import Link from 'components/common/A'
import { Dashed } from 'components/common/Divider'
import VenderLink from 'components/common/VenderLink'
import { HideInMobileBox, HideInDesktopBox } from 'components/common/Hidden'
import PhotoCarousel from './PhotoCarousel'
import HorizontalPhotoSlides from './HorizontalPhotoSlides'
import Controls from './Controls'
import PhotoThumb from './PhotoThumb'
import ShareAlbum from './ShareAlbum'
import MetaTags from './MetaTags'

const DynamicSearchPanel = dynamic(() =>
  import('components/common/PopupSearchPanel')
)

const Background = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      background-image: linear-gradient(90deg, ${
        theme.palette.background.disabled
      } 50%, white 50%);
    }
  `}
`

const Container = styled(MaxWidthWrapper)`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const ContentContainer = styled.div`
  ${({ theme }) => `
    flex-grow: 1;
    overflow: auto;

    ${theme.breakpoints.up('lg')} {
      overflow: hidden;
      height: 100vh;
    }
  `}
`

const StyledGrid = styled(Grid)`
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      height: 100%;
    }
  `}
`

const Wrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    background-color: ${theme.palette.background.disabled};
    position: relative;

    ${theme.breakpoints.up('lg')} {
      height: 100%;
    }
  `}
`

const AbsoluteButtonWrapper = styled.div`
  position: absolute;
  right: 24px;
  bottom: 24px;
  z-index: 1;
`

const AbsolutePhotoCountWrapper = styled.div`
  position: absolute;
  right: 8px;
  bottom: 10px;
  z-index: 1;
`
const AbsolutePhotoCount = styled.div`
  ${({ theme }) => `
    display: inline-block;
    color: white;
    background-color: rgba(0, 0, 0, 0.7);
    font-size: 10px;
    border-radius: 24px;
    padding: 2px 5px;
  `}
`
const PhotoCount = ({ photos, photoUrlId }) => {
  if (!photos?.length || !photoUrlId) {
    return null
  }
  const index = photos?.findIndex((photo) => photo.urlId === photoUrlId)
  return index > -1 ? `${index + 1} / ${photos?.length}` : null
}

const PhotoDescription = styled(ContentWrapper)`
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      text-align: center;
    }
  `}
`

const LgHeight100Box = styled(Box)`
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      height: 100%;
    }
  `}
`
const FlexWrapper = styled(LgHeight100Box)`
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      height: 100%;
    }
  `}
`

const InfoWrapper = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column-reverse;
    padding: 0 ${theme.spacing(4)};

    ${theme.breakpoints.up('lg')} {
      padding: 0 ${theme.spacing(12)} 0 ${theme.spacing(4)};
      flex-grow: 1;
      overflow-y: auto;
      flex-direction: column;
    }
  `}
`

const RoundedButton = styled(Button)`
  ${({ theme }) => `
    & {
      padding-top: 8px;
      padding-bottom: 8px;
      border-radius: 20px;
    }
  `}
`

const genDescByTags = (tagsGroupByCategory = []) => {
  return tagsGroupByCategory
    ?.filter((data) => includes(['風格', '空間'], data.category.name))
    ?.map((data) => data.tags.map((tag) => tag.name).join(' '))
    ?.join(' ')
}

const AlbumContent = ({ onClose, closeModal, fallbackData, back }) => {
  const router = useRouter()

  const albumUrlId = router.query.albumUrlId
  const { data: album } = useAlbum(albumUrlId, {
    fallbackData: fallbackData?.album && [fallbackData?.album],
  })

  const { data: photos, isLoading: isPhotosLoading } = usePhotos(
    album?._id,
    { limit: 999 },
    { fallbackData: fallbackData?.photos }
  )

  const { data: tagCategories } = usePhotoClassifications(
    {},
    { fallbackData: fallbackData?.photoClassifications }
  )

  const [photoUrlId, setPhotoUrlId] = useState(fallbackData?.photoUrlId || null)
  useEffect(() => {
    const photoNeedsUpdate = !photoUrlId || photoUrlId !== router.query.photoId
    if (router.isReady && albumUrlId && !isPhotosLoading && photoNeedsUpdate) {
      const targetPhotoUrlId = photoUrlId || photos?.[0]?.urlId
      const { albumUrlId, ...rest } = router.query

      const qs = window.location.href.split('?')?.[1] || ''
      const newQs = qs
        .split('&')
        .filter((string) => string && !/^(utm_|photoId=|PhotoId=)/.test(string))
        .concat([`photoId=${targetPhotoUrlId}`])
        .join('&')
      window.history.replaceState(window.history.state, '', `?${newQs}`)
      router.query.photoId = targetPhotoUrlId
      setPhotoUrlId(targetPhotoUrlId)
    }
  }, [router.isReady, photos, isPhotosLoading, photoUrlId])

  const findPhoto = (items, urlId) =>
    items?.find?.((item) => item.urlId === urlId)
  const [photo, setPhoto] = useState(findPhoto(photos, photoUrlId))
  useEffect(() => {
    const newPhoto = findPhoto(photos, photoUrlId)
    if (newPhoto?._id !== photo?._id) {
      setPhoto(newPhoto)
    }
  }, [photos, photoUrlId])
  useEffect(() => {
    if (photo) {
      setPhotoViewed(photo._id)
    }
  }, [photo])

  const photoViews = photo?.publicViews ?? 0

  const tagsGroupByCategory = (tagCategories || [])
    .map((category) => ({
      category,
      tags: photo?.tags?.filter(
        (tag) => tag.photoClassification === category._id
      ),
    }))
    .filter((data) => data?.tags?.length)

  const onCloseClick = (e) => {
    if (onClose) {
      onClose(e)
    } else if (back) {
      router.back()
    } else {
      const path = router.query.rt
        ? decodeURIComponent(router.query.rt)
        : '/album'
      router.push(path)
    }
  }

  const [isDescriptionExpand, setIsDescriptionExpand] = useState(false)

  const webviewName = useSelector(selectWebviewName)
  useEffect(() => {
    if (webviewName === 'lsApp') {
      try {
        enableNativeHeaderMainTabs(false)
        enableNativeHeaderNavBack(true)
        enableNativeHeaderSearch(false)
      } catch (err) {
        console.error('native notifier error: ', err.message)
      }
    }
  }, [webviewName])

  const onTagClick = () => closeModal && closeModal()

  useGtmAlbumPageView(album, photos, photoUrlId)

  const isInModal = onClose || closeModal
  const { searchPanelOpen, onSearchPanelClose } = useSearchPanel(webviewName)

  return (
    <>
      {isInModal && router.isReady && !!album && (
        <MetaTags
          resolvedUrl={router.asPath}
          album={album}
          photos={photos}
          photoClassifications={tagCategories}
          photoUrlId={photoUrlId}
        />
      )}
      <Background>
        <Container>
          {webviewName !== 'lsApp' && (
            <HideInDesktopBox display="flex" alignItems="center" my={1}>
              <Box flexGrow={1} textAlign="center" overflow="hidden" pl={2}>
                <Typography variant="h6">
                  <NowrapDiv>{album?.name}</NowrapDiv>
                </Typography>
              </Box>
              <IconButton onClick={onCloseClick} size="large">
                <CloseSVG />
              </IconButton>
            </HideInDesktopBox>
          )}
          <ContentContainer>
            <StyledGrid container>
              <StyledGrid item xs={12} lg={8} xl={9}>
                <Wrapper>
                  <Box height="calc(100% - 68px)" position="relative">
                    <PhotoCarousel
                      photos={photos}
                      photoUrlId={photoUrlId}
                      frontCoverUrlId={fallbackData?.photoUrlId}
                      setPhotoUrlId={setPhotoUrlId}
                    />
                    <HideInDesktopBox>
                      <AbsolutePhotoCountWrapper>
                        <AbsolutePhotoCount>
                          <PhotoCount photos={photos} photoUrlId={photoUrlId} />
                        </AbsolutePhotoCount>
                      </AbsolutePhotoCountWrapper>
                    </HideInDesktopBox>
                  </Box>
                  <div>
                    <PhotoDescription py={{ xs: 4, lg: 6 }} color="text.minus2">
                      <Typography>
                        <HideInMobileBox
                          fontWeight="fontWeightBold"
                          mr={3}
                          component="span"
                        >
                          <PhotoCount photos={photos} photoUrlId={photoUrlId} />
                        </HideInMobileBox>
                        <Box component="span">
                          {photo?.description ||
                            genDescByTags(tagsGroupByCategory)}
                        </Box>
                      </Typography>
                    </PhotoDescription>
                  </div>

                  <HideInMobileBox>
                    <AbsoluteButtonWrapper>
                      <ShareAlbum album={album} photoUrlId={photoUrlId} />
                    </AbsoluteButtonWrapper>
                  </HideInMobileBox>
                </Wrapper>
              </StyledGrid>

              <StyledGrid item xs={12} lg={4} xl={3}>
                <HideInDesktopBox my={4} minHeight="58px">
                  <HorizontalPhotoSlides
                    photos={photos}
                    photoUrlId={photoUrlId}
                    setPhotoUrlId={setPhotoUrlId}
                  />
                </HideInDesktopBox>
                <FlexWrapper display="flex" flexDirection="column">
                  <HideInMobileBox
                    display="flex"
                    justifyContent="flex-end"
                    px={4}
                    pt={4}
                    pm={6}
                  >
                    <RoundedButton
                      variant="contained"
                      size="small"
                      onClick={onCloseClick}
                    >
                      <Box display="flex" alignItems="center">
                        <CloseSVG white />
                        <Box ml={1}>
                          <Typography>返回</Typography>
                        </Box>
                      </Box>
                    </RoundedButton>
                  </HideInMobileBox>
                  <InfoWrapper>
                    <HideInMobileBox mt={5} mb={4}>
                      <Typography variant="h3">
                        <Box color="text.minus2">{album?.name}</Box>
                      </Typography>
                    </HideInMobileBox>

                    <Box>
                      {(!!album?.description || !!album?.vender) && (
                        <HideInDesktopBox>
                          <Dashed my={4} />
                        </HideInDesktopBox>
                      )}
                      {!!album?.vender && (
                        <Box mb={4}>
                          <VenderLink vender={album?.vender} />
                        </Box>
                      )}
                      {!!album?.description && (
                        <>
                          <Typography color="textSecondary">
                            {isDescriptionExpand ? (
                              <>{album.description}</>
                            ) : (
                              <NowrapDiv $lines={2}>
                                {album.description}
                              </NowrapDiv>
                            )}
                          </Typography>
                          {!isDescriptionExpand && (
                            <Box onClick={() => setIsDescriptionExpand(true)}>
                              <Typography color="primary">
                                <Box display="flex" alignItems="center" mt={1}>
                                  展開
                                  <ExpandMoreIcon />
                                </Box>
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                      <HideInMobileBox pt={6}>
                        <Controls album={album} closeModal={closeModal} />
                      </HideInMobileBox>
                    </Box>

                    <Box>
                      <Dashed mt={{ xs: 0, lg: 4 }} mb={4} />
                      <Typography color="textSecondary">
                        <Box
                          display="flex"
                          justifyContent={{ xs: 'none', lg: 'space-between' }}
                          alignItems="center"
                          mb={4}
                        >
                          <Box fontWeight="fontWeightBold" mr={6}>
                            圖片資訊
                          </Box>
                          <Box display="flex" alignItems="center">
                            <EyeSVG textSecondary />
                            <Box ml={2.5}>{photoViews}</Box>
                          </Box>
                        </Box>
                      </Typography>
                      <Typography color="textSecondary">
                        {tagsGroupByCategory?.map((data) => (
                          <Box key={data.category._id} display="flex" mb={2}>
                            <Box>
                              <Box
                                p={3}
                                bgcolor="background.gray"
                                fontWeight="fontWeightBold"
                                whiteSpace="nowrap"
                              >
                                {data.category.name}
                              </Box>
                            </Box>
                            <Box display="flex" flexWrap="wrap" pl={2.5}>
                              {data.tags?.map((tag, index) => (
                                <Fragment key={tag._id}>
                                  {index > 0 && (
                                    <Box py={3} px={2.5}>
                                      |
                                    </Box>
                                  )}
                                  <Box py={3}>
                                    <Link
                                      href={`/album?tag=${tag.urlId}`}
                                      $textSecondary
                                      onClick={onTagClick}
                                    >
                                      <Box whiteSpace="nowrap">{tag.name}</Box>
                                    </Link>
                                  </Box>
                                </Fragment>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Typography>
                    </Box>

                    <HideInMobileBox component="span">
                      <Box>
                        <Dashed mt={4} />
                        <Typography color="textSecondary">
                          <Box fontWeight="fontWeightBold" my={6}>
                            圖片系列照
                          </Box>
                        </Typography>
                        <Grid container spacing={2}>
                          {photos?.map((photo) => (
                            <Grid item key={photo._id}>
                              <PhotoThumb
                                photo={photo}
                                isSelected={photoUrlId === photo.urlId}
                                onClick={() => setPhotoUrlId(photo.urlId)}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Box mt={12} />
                      </Box>
                    </HideInMobileBox>
                  </InfoWrapper>
                </FlexWrapper>
              </StyledGrid>
            </StyledGrid>
          </ContentContainer>

          <HideInDesktopBox bgcolor="white">
            <Controls album={album} closeModal={closeModal} />
          </HideInDesktopBox>
        </Container>
      </Background>

      <DynamicSearchPanel
        open={!isInModal && webviewName === 'lsApp' && searchPanelOpen}
        onClose={onSearchPanelClose}
      />
    </>
  )
}

export default AlbumContent
